import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUsers, addUser, updateUserPassword, deleteUser } from '../utils/auth';
import { UsersIcon, FingerPrintIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

// Side navigation items with conditional inclusion based on role
const getSideNavigation = (userRole) => {
  const items = [
    { name: 'Team', href: '/admin/team', icon: UsersIcon, current: true },
    { name: 'Security', href: '/admin/security', icon: FingerPrintIcon, current: false },
  ];
  
  return items;
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Team component for admin users to manage other users
 */
const Team = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // New user form state
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('read');
  const [isAddingUser, setIsAddingUser] = useState(false);
  
  // Edit user state
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserPassword, setEditUserPassword] = useState('');
  const [isEditingUser, setIsEditingUser] = useState(false);
  
  // Load users on mount
  useEffect(() => {
    fetchUsers();
  }, []);
  
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const fetchedUsers = await getUsers();
      setUsers(fetchedUsers);
      setError('');
    } catch (err) {
      setError('Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!newUserEmail || !newUserPassword) {
      setError('Email and password are required');
      return;
    }
    
    setIsAddingUser(true);
    setError('');
    setSuccess('');
    
    try {
      await addUser(newUserEmail, newUserPassword, newUserRole);
      setSuccess(`User ${newUserEmail} added successfully`);
      
      // Reset form
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('read');
      
      // Refresh user list
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to add user');
    } finally {
      setIsAddingUser(false);
    }
  };
  
  const handleEditUser = (email) => {
    setEditUserEmail(email);
    setEditUserPassword('');
    setIsEditingUser(true);
  };
  
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (!editUserPassword) {
      setError('New password is required');
      return;
    }
    
    setError('');
    setSuccess('');
    
    try {
      await updateUserPassword(editUserEmail, editUserPassword);
      setSuccess(`Password updated for ${editUserEmail}`);
      
      // Reset form
      setEditUserEmail('');
      setEditUserPassword('');
      setIsEditingUser(false);
    } catch (err) {
      setError(err.message || 'Failed to update password');
    }
  };
  
  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}?`)) {
      return;
    }
    
    setError('');
    setSuccess('');
    
    try {
      await deleteUser(email);
      setSuccess(`User ${email} deleted successfully`);
      
      // Refresh user list
      fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    }
  };
  
  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-700"></div>
      </div>
    );
  }
  
  return (
    <div className="rounded-lg bg-white shadow-sm">
      <div className="mx-auto max-w-7xl pt-4 lg:flex lg:gap-x-16 lg:px-8">
        <h1 className="sr-only">User Management</h1>

        {/* Sidebar navigation */}
        <aside className="flex overflow-x-auto border-b border-gray-900/5 py-2 lg:block lg:w-64 lg:flex-none lg:border-0 lg:py-8">
          <nav className="flex-none px-4 sm:px-6 lg:px-0">
            <ul role="list" className="flex gap-x-3 gap-y-1 whitespace-nowrap lg:flex-col">
              {getSideNavigation(currentUser?.role).map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={classNames(
                      item.current
                        ? 'bg-gray-50 text-indigo-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                      'group flex gap-x-3 rounded-md py-2 pr-3 pl-2 text-sm/6 font-semibold',
                    )}
                  >
                    <item.icon
                      aria-hidden="true"
                      className={classNames(
                        item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                        'size-6 shrink-0',
                      )}
                    />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Main content */}
        <main className="px-4 py-4 sm:px-6 lg:flex-auto lg:px-0 lg:py-8">
          <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none">
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}
            
            {success && (
              <div className="rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">{success}</h3>
                  </div>
                </div>
              </div>
            )}
            
            {/* User Management Section */}
            <div>
              <h2 className="text-base/7 font-semibold text-gray-900">Manage Users</h2>
              <p className="mt-1 text-sm/6 text-gray-500">
                View and manage existing user accounts in the system
              </p>

              <ul role="list" className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm/6">
                {users.map((user) => (
                  <li key={user.email} className="flex justify-between gap-x-6 py-6">
                    <div>
                      <div className="font-medium text-gray-900">{user.email}</div>
                      <div className="text-gray-500 mt-1 capitalize">{user.role.replace('_', ' ')}</div>
                    </div>
                    <div className="flex gap-x-4">
                      <button 
                        type="button" 
                        onClick={() => handleEditUser(user.email)}
                        className="font-semibold text-indigo-600 hover:text-indigo-500"
                      >
                        Reset Password
                      </button>
                      <button 
                        type="button" 
                        onClick={() => handleDeleteUser(user.email)}
                        className="font-semibold text-red-600 hover:text-red-500"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
                {users.length === 0 && (
                  <li className="py-6 text-center text-gray-500">
                    No users found
                  </li>
                )}
              </ul>
            </div>

            {/* Add New User Section */}
            <div>
              <h2 className="text-base/7 font-semibold text-gray-900">Add New User</h2>
              <p className="mt-1 text-sm/6 text-gray-500">
                Create a new user account with appropriate permissions
              </p>
              
              <form onSubmit={handleAddUser} className="mt-6">
                <div className="space-y-6 divide-y divide-gray-100 border-t border-gray-200">
                  <div className="py-6 sm:flex">
                    <label htmlFor="new-email" className="text-sm font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                      Email Address
                    </label>
                    <div className="mt-1 flex flex-col sm:mt-0 sm:flex-auto">
                      <input
                        id="new-email"
                        type="email"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        required
                        className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 shadow-sm outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                  
                  <div className="py-6 sm:flex">
                    <label htmlFor="new-password" className="text-sm font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                      Password
                    </label>
                    <div className="mt-1 flex flex-col sm:mt-0 sm:flex-auto">
                      <input
                        id="new-password"
                        type="password"
                        value={newUserPassword}
                        onChange={(e) => setNewUserPassword(e.target.value)}
                        required
                        className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 shadow-sm outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                  
                  <div className="py-6 sm:flex">
                    <label htmlFor="new-role" className="text-sm font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                      Role
                    </label>
                    <div className="mt-1 flex flex-col sm:mt-0 sm:flex-auto">
                      <div className="relative">
                        <select
                          id="new-role"
                          value={newUserRole}
                          onChange={(e) => setNewUserRole(e.target.value)}
                          className="block w-full rounded-md border border-gray-300 bg-white py-1.5 px-3 text-base text-gray-900 shadow-sm outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 appearance-none pr-8"
                        >
                          <option value="read">Read Only</option>
                          <option value="read_write">Read/Write</option>
                          <option value="admin">Admin</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 text-gray-400">
                            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex pt-6 border-t border-gray-100">
                  <button
                    type="submit"
                    disabled={isAddingUser}
                    className="ml-auto rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-indigo-300"
                  >
                    {isAddingUser ? 'Adding...' : 'Add User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>

        {/* Edit User Modal */}
        {isEditingUser && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
              <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
              <div className="inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
                <div>
                  <h3 className="text-base/7 font-semibold text-gray-900">Update Password</h3>
                  <p className="mt-1 text-sm/6 text-gray-500">
                    Set a new password for the selected user
                  </p>
                  <form onSubmit={handleUpdatePassword} className="mt-6">
                    <div className="space-y-6 divide-y divide-gray-100">
                      <div className="py-6 sm:flex">
                        <label htmlFor="edit-email" className="text-sm font-medium text-gray-900 sm:w-32 sm:flex-none sm:pr-6">
                          Email
                        </label>
                        <div className="mt-1 flex flex-col sm:mt-0 sm:flex-auto">
                          <input
                            id="edit-email"
                            type="email"
                            value={editUserEmail}
                            disabled
                            className="block w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1.5 text-gray-700 shadow-sm outline-1 -outline-offset-1 outline-gray-300 sm:text-sm/6"
                          />
                        </div>
                      </div>
                      <div className="py-6 sm:flex">
                        <label htmlFor="edit-password" className="text-sm font-medium text-gray-900 sm:w-32 sm:flex-none sm:pr-6">
                          New Password
                        </label>
                        <div className="mt-1 flex flex-col sm:mt-0 sm:flex-auto">
                          <input
                            id="edit-password"
                            type="password"
                            value={editUserPassword}
                            onChange={(e) => setEditUserPassword(e.target.value)}
                            required
                            className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 shadow-sm outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex items-center justify-end gap-x-6">
                      <button
                        type="button"
                        className="text-sm/6 font-semibold text-gray-900"
                        onClick={() => setIsEditingUser(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      >
                        Update
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Team;